/* =========================================================
   TD PREMIUM SLIDE CART — V1
   Bestand: assets/td-slide-cart.js

   DEEL 1 VAN 5
   Basis, openen/sluiten, focusbeheer en API-helpers.

   BELANGRIJK:
   - Plak dit vanaf regel 1 in td-slide-cart.js.
   - De volgende delen worden hier direct onder geplakt.
   - Test de volledige Slide Cart pas nadat alle 5 delen erin staan.
   ========================================================= */

(() => {
  'use strict';

  /**
   * Voorkomt dat het script dubbel wordt geïnitialiseerd.
   * Dit is handig in de Shopify Theme Editor, waar sections
   * opnieuw geladen kunnen worden zonder een volledige paginareload.
   */
  if (window.TDSlideCart?.initialized) {
    return;
  }

  /**
   * Centrale configuratie.
   * De waarden worden later aangevuld vanuit data-attributen
   * in snippets/td-slide-cart.liquid.
   */
  const CONFIG = {
    rootSelector: '[data-td-slide-cart]',
    openClass: 'is-open',
    pageOpenClass: 'tdsc-is-open',
    defaultCartAddUrl: '/cart/add.js',
    defaultCartChangeUrl: '/cart/change.js',
    defaultCartUpdateUrl: '/cart/update.js',
    defaultCartJsonUrl: '/cart.js',
    defaultCartUrl: '/cart',
    defaultCheckoutUrl: '/checkout',
    noticeDuration: 3200,
    quantityDebounce: 260
  };

  /**
   * Interne state.
   */
  const state = {
    root: null,
    drawer: null,
    backdrop: null,
    loading: null,
    content: null,
    items: null,
    empty: null,
    footer: null,
    subtotal: null,
    notice: null,
    status: null,
    template: null,

    routes: {
      add: CONFIG.defaultCartAddUrl,
      change: CONFIG.defaultCartChangeUrl,
      update: CONFIG.defaultCartUpdateUrl,
      cartJson: CONFIG.defaultCartJsonUrl,
      cart: CONFIG.defaultCartUrl,
      checkout: CONFIG.defaultCheckoutUrl
    },

    moneyFormat: '€{{amount_with_comma_separator}}',
    cart: null,
    isOpen: false,
    isLoading: false,
    isUpdating: false,
    lastFocusedElement: null,
    quantityTimer: null,
    noticeTimer: null
  };

  /**
   * Handige query helpers.
   */
  const qs = (selector, scope = document) => scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    Array.from(scope.querySelectorAll(selector));

  /**
   * Controleert of een element zichtbaar en focusbaar is.
   */
  const isVisible = (element) => {
    if (!element) return false;

    const style = window.getComputedStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      !element.hasAttribute('hidden') &&
      element.getClientRects().length > 0
    );
  };

  /**
   * Haalt alle focusbare elementen binnen de drawer op.
   */
  const getFocusableElements = () => {
    if (!state.root) return [];

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return qsa(selector, state.root).filter(isVisible);
  };

  /**
   * Zet loading state aan of uit.
   */
  const setLoading = (active) => {
    state.isLoading = Boolean(active);

    if (!state.loading) return;

    state.loading.hidden = !active;
    state.root?.classList.toggle('is-loading', Boolean(active));
    state.root?.setAttribute('aria-busy', String(Boolean(active)));
  };

  /**
   * Toont een succes- of foutmelding.
   */
  const showNotice = (message = '', type = 'success') => {
    if (!state.notice) return;

    window.clearTimeout(state.noticeTimer);

    if (!message) {
      state.notice.hidden = true;
      state.notice.textContent = '';
      state.notice.classList.remove('is-error');
      return;
    }

    state.notice.textContent = message;
    state.notice.hidden = false;
    state.notice.classList.toggle('is-error', type === 'error');

    state.noticeTimer = window.setTimeout(() => {
      if (!state.notice) return;
      state.notice.hidden = true;
    }, CONFIG.noticeDuration);
  };

  /**
   * Geeft een toegankelijke status door aan screenreaders.
   */
  const announce = (message = '') => {
    if (!state.status) return;

    state.status.textContent = message || 'Jouw bestelling';
  };

  /**
   * Format Shopify-centen naar valuta.
   */
  const formatMoney = (cents = 0) => {
    const amount = Number(cents || 0);

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      try {
        return window.Shopify.formatMoney(amount, state.moneyFormat);
      } catch (error) {
        console.warn('[TD Slide Cart] Shopify.formatMoney mislukt:', error);
      }
    }

    const activeCurrency =
      window.Shopify?.currency?.active ||
      document.documentElement.dataset.currency ||
      'EUR';

    const locale =
      document.documentElement.lang ||
      navigator.language ||
      'nl-NL';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency
      }).format(amount / 100);
    } catch (error) {
      return `€${(amount / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  /**
   * Maakt een Shopify-afbeeldings-URL geschikt voor het gewenste formaat.
   */
  const resizeImageUrl = (url, width = 240) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  };

  /**
   * Veilige JSON fetch helper.
   */
  const requestJSON = async (url, options = {}) => {
    const fetchOptions = {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body && !(options.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(options.headers || {})
      },
      ...options
    };

    let response;

    try {
      response = await fetch(url, fetchOptions);
    } catch (error) {
      throw new Error(
        'De verbinding met de winkelwagen is mislukt. Controleer je internetverbinding.'
      );
    }

    let data = {};

    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }

    if (!response.ok) {
      const message =
        data.description ||
        data.message ||
        data.errors ||
        'Er ging iets mis met de winkelwagen.';

      throw new Error(
        typeof message === 'string'
          ? message
          : 'Er ging iets mis met de winkelwagen.'
      );
    }

    return data;
  };

  /**
   * Leest routes en instellingen uit de Liquid-markup.
   */
  const readRootConfiguration = () => {
    if (!state.root) return;

    const dataset = state.root.dataset;
    const liquidConfig = window.TDSlideCartConfig || {};
    const liquidRoutes = liquidConfig.routes || {};

    state.routes.add =
      dataset.cartAddUrl || liquidRoutes.cartAdd || liquidRoutes.add || CONFIG.defaultCartAddUrl;

    state.routes.change =
      dataset.cartChangeUrl || liquidRoutes.cartChange || liquidRoutes.change || CONFIG.defaultCartChangeUrl;

    state.routes.update =
      dataset.cartUpdateUrl || liquidRoutes.cartUpdate || liquidRoutes.update || CONFIG.defaultCartUpdateUrl;

    state.routes.cartJson =
      dataset.cartJsonUrl || liquidRoutes.cartJson || CONFIG.defaultCartJsonUrl;

    state.routes.cart =
      dataset.cartUrl || liquidRoutes.cart || CONFIG.defaultCartUrl;

    state.routes.checkout =
      dataset.checkoutUrl || liquidRoutes.checkout || CONFIG.defaultCheckoutUrl;

    state.moneyFormat =
      dataset.moneyFormat || liquidConfig.moneyFormat || state.moneyFormat;
  };

  /**
   * Slaat alle benodigde DOM-elementen op.
   */
  const cacheElements = () => {
    state.root = qs(CONFIG.rootSelector);

    if (!state.root) {
      return false;
    }

    state.drawer = qs('.tdsc__drawer', state.root);
    state.backdrop = qs('.tdsc__backdrop', state.root);
    state.loading = qs('[data-tdsc-loading], [data-cart-loading]', state.root);
    state.content = qs('[data-tdsc-content], [data-cart-content]', state.root);
    state.items = qs('[data-tdsc-items], [data-cart-items]', state.root);
    state.empty = qs('[data-tdsc-empty], [data-cart-empty]', state.root);
    state.footer = qs('[data-tdsc-footer], [data-cart-footer]', state.root);
    state.subtotal = qs('[data-tdsc-subtotal], [data-cart-subtotal]', state.root);
    state.notice = qs('[data-tdsc-notice], [data-cart-notice]', state.root);
    state.status = qs('[data-tdsc-status], [data-cart-live-region]', state.root);
    state.template = qs('[data-tdsc-item-template], [data-cart-item-template]', state.root);

    readRootConfiguration();

    return true;
  };

  /**
   * Focus trap voor toegankelijkheid.
   */
  const trapFocus = (event) => {
    if (
      event.key !== 'Tab' ||
      !state.isOpen ||
      !state.root
    ) {
      return;
    }

    const focusable = getFocusableElements();

    if (!focusable.length) {
      event.preventDefault();
      state.drawer?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Opent de drawer.
   *
   * refreshCart wordt in DEEL 2 toegevoegd.
   * Daarom wordt hier gecontroleerd of de functie al bestaat.
   */
  const openCart = async ({ message = '', refresh = true } = {}) => {
    if (!state.root || !state.drawer) return;

    state.lastFocusedElement = document.activeElement;
    state.isOpen = true;

    state.root.classList.add(CONFIG.openClass);
    state.root.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add(CONFIG.pageOpenClass);
    document.body.classList.add(CONFIG.pageOpenClass);

    announce(message || 'Jouw bestelling');

    if (message) {
      showNotice(message, 'success');
    }

    if (
      refresh &&
      typeof api.refresh === 'function'
    ) {
      try {
        await api.refresh();
      } catch (error) {
        console.error('[TD Slide Cart] Vernieuwen mislukt:', error);
      }
    }

    window.requestAnimationFrame(() => {
      state.drawer?.focus({ preventScroll: true });
    });

    document.dispatchEvent(
      new CustomEvent('td:slide-cart-opened', {
        detail: { cart: state.cart }
      })
    );
  };

  /**
   * Sluit de drawer.
   */
  const closeCart = () => {
    if (!state.root) return;

    state.isOpen = false;

    state.root.classList.remove(CONFIG.openClass);
    state.root.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove(CONFIG.pageOpenClass);
    document.body.classList.remove(CONFIG.pageOpenClass);

    showNotice('');

    if (
      state.lastFocusedElement &&
      typeof state.lastFocusedElement.focus === 'function'
    ) {
      window.setTimeout(() => {
        state.lastFocusedElement?.focus({ preventScroll: true });
      }, 50);
    }

    document.dispatchEvent(
      new CustomEvent('td:slide-cart-closed')
    );
  };

  /**
   * ESC en focus trap.
   */
  const handleRootKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCart();
      return;
    }

    trapFocus(event);
  };

  /**
   * Globale clicks:
   * - sluiten via data-tdsc-close
   * - openen via data-tdsc-open
   * - winkelwagenlink onderscheppen
   */
  const handleDocumentClick = (event) => {
    const closeTrigger = event.target.closest('[data-tdsc-close], [data-cart-close]');

    if (closeTrigger) {
      event.preventDefault();
      closeCart();
      return;
    }

    const explicitOpenTrigger =
      event.target.closest('[data-tdsc-open], [data-cart-open]');

    if (explicitOpenTrigger) {
      event.preventDefault();
      openCart();
      return;
    }

    const cartLink = event.target.closest(
      'a[href="/cart"], a[href$="/cart"], a[href*="/cart?"]'
    );

    if (
      cartLink &&
      !cartLink.closest(CONFIG.rootSelector) &&
      !cartLink.hasAttribute('data-tdsc-ignore')
    ) {
      event.preventDefault();
      openCart();
    }
  };

  /**
   * Publieke API.
   * In volgende delen worden refresh, add, change en render toegevoegd.
   */
  const api = {
    initialized: true,
    open: openCart,
    close: closeCart,
    refresh: null,
    add: null,
    change: null,
    render: null,
    formatMoney,
    resizeImageUrl,
    requestJSON,
    get state() {
      return state;
    }
  };

  /**
   * Initialisatie van Deel 1.
   */
  const init = () => {
    if (!cacheElements()) {
      console.warn(
        '[TD Slide Cart] Geen element met data-td-slide-cart gevonden. ' +
        'Dit is normaal totdat td-slide-cart.liquid in theme.liquid wordt gerenderd.'
      );

      window.TDSlideCart = api;
      return;
    }

    if (state.root.dataset.initialized === 'true') {
      window.TDSlideCart = api;
      return;
    }

    state.root.dataset.initialized = 'true';
    state.root.setAttribute('aria-hidden', 'true');

    state.root.addEventListener('keydown', handleRootKeydown);
    document.addEventListener('click', handleDocumentClick);

    /**
     * Externe events voor theme-integraties.
     */
    document.addEventListener('td:open-cart', () => {
      openCart();
    });

    document.addEventListener('td:close-cart', () => {
      closeCart();
    });

    window.TDSlideCart = api;

    document.dispatchEvent(
      new CustomEvent('td:slide-cart-ready', {
        detail: { api }
      })
    );
  };

  /**
   * Shopify Theme Editor kan content dynamisch herladen.
   */
  document.addEventListener('shopify:section:load', () => {
    if (!window.TDSlideCart?.state?.root) {
      cacheElements();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {
      once: true
    });
  } else {
    init();
  }

  /* =========================================================
     EINDE DEEL 1 VAN 5

     DEEL 2 HIER DIRECT ONDER PLAKKEN.
     Verwijder de afsluitende regel hieronder nog niet:
     ========================================================= */
/* =========================================================
   TD PREMIUM SLIDE CART — V1
   DEEL 2 VAN 5

   Producten ophalen, renderen en cart-state verversen.

   PLAATSING:
   Plak dit direct onder DEEL 1 in assets/td-slide-cart.js.
   ========================================================= */

  /**
   * Maakt een DOM-element met optionele class en tekst.
   */
  const createElement = (tagName, className = '', text = '') => {
    const element = document.createElement(tagName);

    if (className) {
      element.className = className;
    }

    if (text !== '') {
      element.textContent = text;
    }

    return element;
  };

  /**
   * Geeft de beste beschikbare afbeelding van een cart-item terug.
   */
  const getItemImage = (item) => {
    return (
      item?.featured_image?.url ||
      item?.featured_image ||
      item?.image ||
      ''
    );
  };

  /**
   * Geeft de beste beschikbare alt-tekst terug.
   */
  const getItemImageAlt = (item) => {
    return (
      item?.featured_image?.alt ||
      item?.product_title ||
      item?.title ||
      'Productafbeelding'
    );
  };

  /**
   * Verwijdert niet-zichtbare line-item properties.
   * Shopify gebruikt een underscore-prefix voor interne properties.
   */
  const getVisibleProperties = (properties) => {
    if (
      !properties ||
      typeof properties !== 'object'
    ) {
      return [];
    }

    return Object.entries(properties).filter(([key, value]) => {
      return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== '' &&
        !String(key).startsWith('_')
      );
    });
  };

  /**
   * Bouwt de properties-weergave.
   */
  const renderItemProperties = (container, properties) => {
    if (!container) return;

    const entries = getVisibleProperties(properties);

    container.innerHTML = '';

    if (!entries.length) {
      container.hidden = true;
      return;
    }

    entries.forEach(([key, value]) => {
      const row = createElement('span');

      /**
       * Shopify file-upload properties kunnen een URL bevatten.
       * In dat geval tonen we een veilige link.
       */
      if (
        typeof value === 'string' &&
        /^https?:\/\//i.test(value)
      ) {
        const label = createElement('span', '', `${key}: `);
        const link = createElement('a', 'tdsc__property-link', 'Bekijken');

        link.href = value;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        row.append(label, link);
      } else {
        row.textContent = `${key}: ${value}`;
      }

      container.appendChild(row);
    });

    container.hidden = false;
  };

  /**
   * Leest de varianttitel.
   * "Default Title" wordt niet getoond.
   */
  const getVisibleVariantTitle = (item) => {
    const variantTitle = String(item?.variant_title || '').trim();

    if (
      !variantTitle ||
      variantTitle.toLowerCase() === 'default title'
    ) {
      return '';
    }

    return variantTitle;
  };

  /**
   * Maakt een productlink veilig bruikbaar.
   */
  const getItemUrl = (item) => {
    const url = item?.url || item?.product_url || '';

    if (!url) {
      return state.routes.cart;
    }

    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  };

  /**
   * Toont of verbergt de drie hoofdtoestanden:
   * loading, lege cart en gevulde cart.
   */
  const showCartView = (view) => {
    const isLoading = view === 'loading';
    const isEmpty = view === 'empty';
    const hasItems = view === 'items';

    if (state.loading) {
      state.loading.hidden = !isLoading;
    }

    if (state.empty) {
      state.empty.hidden = !isEmpty;
    }

    if (state.items) {
      state.items.hidden = !hasItems;
    }

    if (state.footer) {
      state.footer.hidden = !hasItems;
    }

    state.root?.classList.toggle('is-empty', isEmpty);
    state.root?.classList.toggle('has-items', hasItems);
  };

  /**
   * Maakt een fallback-kaart wanneer het HTML-template ontbreekt.
   * In de normale installatie wordt het template uit Liquid gebruikt.
   */
  const createFallbackItemCard = () => {
    const article = createElement('article', 'tdsc__item');

    article.innerHTML = `
      <a class="tdsc__item-media" data-item-url>
        <img data-item-image loading="lazy" alt="">
      </a>

      <div class="tdsc__item-main">
        <div class="tdsc__item-top">
          <div class="tdsc__item-copy">
            <a class="tdsc__item-title" data-item-url data-item-title></a>
            <p class="tdsc__item-variant" data-item-variant hidden></p>
            <div class="tdsc__item-properties" data-item-properties hidden></div>
          </div>

          <button
            type="button"
            class="tdsc__remove"
            data-item-remove
            aria-label="Product verwijderen"
          >
            ×
          </button>
        </div>

        <div class="tdsc__item-bottom">
          <div class="tdsc__quantity">
            <button
              type="button"
              data-item-decrease
              aria-label="Aantal verlagen"
            >
              −
            </button>

            <input
              type="number"
              min="0"
              value="1"
              inputmode="numeric"
              data-item-quantity
              aria-label="Aantal"
            >

            <button
              type="button"
              data-item-increase
              aria-label="Aantal verhogen"
            >
              +
            </button>
          </div>

          <strong class="tdsc__item-price" data-item-price></strong>
        </div>
      </div>
    `;

    return article;
  };

  /**
   * Clone het Liquid-template.
   */
  const cloneItemTemplate = () => {
    if (
      state.template &&
      state.template.content &&
      state.template.content.firstElementChild
    ) {
      return state.template.content.firstElementChild.cloneNode(true);
    }

    return createFallbackItemCard();
  };

  /**
   * Zet dataset-informatie op een itemkaart.
   * Deel 3 gebruikt deze gegevens voor quantity updates.
   */
  const applyItemDataset = (card, item, index) => {
    card.dataset.itemKey = item?.key || '';
    card.dataset.itemId = String(item?.id || '');
    card.dataset.itemVariantId = String(item?.variant_id || item?.id || '');
    card.dataset.itemIndex = String(index + 1);
    card.dataset.itemQuantity = String(item?.quantity || 0);
  };

  /**
   * Vult één cart-itemkaart met Shopify-data.
   *
   * Event listeners voor +, -, input en verwijderen
   * worden in DEEL 3 toegevoegd via event delegation.
   */
  const createItemCard = (item, index) => {
    const card = cloneItemTemplate();

    applyItemDataset(card, item, index);

    const links = qsa('[data-item-url]', card);
    const image = qs('[data-item-image]', card);
    const title = qs('[data-item-title]', card);
    const variant = qs('[data-item-variant]', card);
    const properties = qs('[data-item-properties]', card);
    const quantity = qs('[data-item-quantity]', card);
    const price = qs('[data-item-price]', card);
    const removeButton = qs('[data-item-remove]', card);
    const decreaseButton = qs('[data-item-decrease]', card);
    const increaseButton = qs('[data-item-increase]', card);

    const itemUrl = getItemUrl(item);
    const itemImage = getItemImage(item);
    const variantTitle = getVisibleVariantTitle(item);

    links.forEach((link) => {
      link.href = itemUrl;
    });

    if (image) {
      if (itemImage) {
        image.src = resizeImageUrl(itemImage, 240);
        image.alt = getItemImageAlt(item);
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = true;
        image.closest('.tdsc__item-media')?.classList.add('is-empty');
      }
    }

    if (title) {
      title.textContent =
        item?.product_title ||
        item?.title ||
        'Product';
    }

    if (variant) {
      if (variantTitle) {
        variant.textContent = variantTitle;
        variant.hidden = false;
      } else {
        variant.textContent = '';
        variant.hidden = true;
      }
    }

    renderItemProperties(properties, item?.properties);

    if (quantity) {
      quantity.value = String(item?.quantity || 1);
      quantity.dataset.itemKey = item?.key || '';
      quantity.dataset.itemIndex = String(index + 1);
      quantity.setAttribute(
        'aria-label',
        `Aantal voor ${item?.product_title || item?.title || 'product'}`
      );
    }

    if (price) {
      const linePrice =
        item?.final_line_price ??
        item?.line_price ??
        0;

      price.textContent = formatMoney(linePrice);
    }

    if (removeButton) {
      removeButton.dataset.itemKey = item?.key || '';
      removeButton.dataset.itemIndex = String(index + 1);
      removeButton.setAttribute(
        'aria-label',
        `${item?.product_title || item?.title || 'Product'} verwijderen`
      );
    }

    if (decreaseButton) {
      decreaseButton.dataset.itemKey = item?.key || '';
      decreaseButton.dataset.itemIndex = String(index + 1);
    }

    if (increaseButton) {
      increaseButton.dataset.itemKey = item?.key || '';
      increaseButton.dataset.itemIndex = String(index + 1);
    }

    /**
     * Cart-item korting tonen als die beschikbaar is.
     */
    if (
      Array.isArray(item?.line_level_discount_allocations) &&
      item.line_level_discount_allocations.length
    ) {
      const discountWrap = createElement(
        'div',
        'tdsc__item-discounts'
      );

      item.line_level_discount_allocations.forEach((allocation) => {
        const titleText =
          allocation?.discount_application?.title ||
          allocation?.title ||
          'Korting';

        const amount =
          allocation?.amount ||
          0;

        const badge = createElement(
          'span',
          'tdsc__item-discount',
          `${titleText}: −${formatMoney(amount)}`
        );

        discountWrap.appendChild(badge);
      });

      qs('.tdsc__item-copy', card)?.appendChild(discountWrap);
    }

    return card;
  };

  /**
   * Update bekende cart-count badges in het thema.
   */
  const updateThemeCartCount = (count = 0) => {
    const normalizedCount = Math.max(0, Number(count) || 0);

    const countSelectors = [
      '[data-cart-count]',
      '[data-cart-count-bubble]',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span:first-child',
      '#cart-icon-bubble .cart-count-bubble span',
      '.header__icon--cart .cart-count-bubble span'
    ];

    countSelectors.forEach((selector) => {
      qsa(selector).forEach((element) => {
        element.textContent = String(normalizedCount);
      });
    });

    qsa('[data-cart-count-label]').forEach((element) => {
      element.textContent =
        normalizedCount === 1
          ? 'artikel in je winkelmandje'
          : 'artikelen in je winkelmandje';
    });

    /**
     * Verberg of toon bekende bubbles.
     */
    qsa('.cart-count-bubble, [data-cart-count-bubble]').forEach((bubble) => {
      bubble.hidden = normalizedCount <= 0;
      bubble.classList.toggle('is-empty', normalizedCount <= 0);
    });

    document.dispatchEvent(
      new CustomEvent('td:cart-count-updated', {
        detail: {
          itemCount: normalizedCount
        }
      })
    );
  };

  /**
   * Werkt de subtotaalprijs bij.
   */
  const updateSubtotal = (cart) => {
    if (!state.subtotal) return;

    state.subtotal.textContent = formatMoney(
      cart?.total_price || 0
    );
  };

  /**
   * Rendert de volledige cart.
   */
  const renderCart = (cart) => {
    if (!state.root) return;

    state.cart = cart || {
      items: [],
      item_count: 0,
      total_price: 0
    };

    const items = Array.isArray(state.cart.items)
      ? state.cart.items
      : [];

    if (state.items) {
      state.items.innerHTML = '';
    }

    updateSubtotal(state.cart);
    updateThemeCartCount(state.cart.item_count || 0);

    if (!items.length) {
      showCartView('empty');
      announce('Je winkelwagen is leeg');

      document.dispatchEvent(
        new CustomEvent('td:cart-rendered', {
          detail: {
            cart: state.cart,
            isEmpty: true
          }
        })
      );

      return;
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      fragment.appendChild(createItemCard(item, index));
    });

    state.items?.appendChild(fragment);

    showCartView('items');
    announce(
      `${state.cart.item_count || items.length} ${
        Number(state.cart.item_count || items.length) === 1
          ? 'product'
          : 'producten'
      } in je winkelwagen`
    );

    document.dispatchEvent(
      new CustomEvent('td:cart-rendered', {
        detail: {
          cart: state.cart,
          isEmpty: false
        }
      })
    );
  };

  /**
   * Haalt de actuele cart op bij Shopify.
   */
  const fetchCart = async () => {
    const separator = state.routes.cartJson.includes('?')
      ? '&'
      : '?';

    const cacheBusterUrl =
      `${state.routes.cartJson}${separator}_tdsc=${Date.now()}`;

    return requestJSON(cacheBusterUrl, {
      method: 'GET',
      cache: 'no-store'
    });
  };

  /**
   * Vernieuwt de cart en rendert hem opnieuw.
   */
  const refreshCart = async ({
    showLoader = true,
    preserveScroll = false
  } = {}) => {
    if (!state.root) {
      return null;
    }

    const previousScrollTop =
      preserveScroll && state.content
        ? state.content.scrollTop
        : 0;

    if (showLoader) {
      showCartView('loading');
      setLoading(true);
    }

    try {
      const cart = await fetchCart();

      renderCart(cart);

      if (
        preserveScroll &&
        state.content
      ) {
        window.requestAnimationFrame(() => {
          state.content.scrollTop = previousScrollTop;
        });
      }

      return cart;
    } catch (error) {
      console.error(
        '[TD Slide Cart] Cart ophalen mislukt:',
        error
      );

      showNotice(
        error.message ||
        'De winkelwagen kon niet worden geladen.',
        'error'
      );

      /**
       * Wanneer er al een geldige cart-state is,
       * tonen we die opnieuw in plaats van een lege loader.
       */
      if (state.cart) {
        renderCart(state.cart);
      } else {
        showCartView('empty');
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cart inladen wanneer de browserpagina opnieuw actief wordt.
   * Dit vangt bijvoorbeeld terugnavigeren vanuit checkout op.
   */
  const handlePageShow = (event) => {
    if (
      event.persisted ||
      document.visibilityState === 'visible'
    ) {
      if (state.isOpen) {
        refreshCart({
          showLoader: false,
          preserveScroll: true
        }).catch(() => {});
      }
    }
  };

  /**
   * Cart verversen wanneer een externe integratie dit vraagt.
   */
  const handleExternalRefresh = () => {
    refreshCart({
      showLoader: false,
      preserveScroll: true
    }).catch(() => {});
  };

  /**
   * Koppel Deel 2 aan de publieke API uit Deel 1.
   */
  api.refresh = refreshCart;
  api.render = renderCart;
  api.fetchCart = fetchCart;

  /**
   * Events van Deel 2.
   */
  window.addEventListener('pageshow', handlePageShow);

  document.addEventListener(
    'td:refresh-cart',
    handleExternalRefresh
  );

  document.addEventListener(
    'cart:refresh',
    handleExternalRefresh
  );

  /**
   * Wanneer de root al bestaat, toon standaard de loader
   * totdat de drawer voor het eerst wordt geopend.
   */
  if (state.root) {
    showCartView('loading');
    setLoading(false);
  }

  /* =========================================================
     EINDE DEEL 2 VAN 5

     DEEL 3 HIER DIRECT ONDER PLAKKEN.
     ========================================================= */
/* =========================================================
   TD PREMIUM SLIDE CART — V1
   DEEL 3 VAN 5

   Quantity wijzigen, producten verwijderen en Ajax-updates.

   PLAATSING:
   Plak dit direct onder DEEL 2 in assets/td-slide-cart.js.
   ========================================================= */


/**
 * Voorkomt meerdere gelijktijdige cart-updates.
 */
let cartMutationInProgress = false;


/**
 * Bewaart timers voor handmatige quantity-inputs.
 */
const quantityInputTimers = new WeakMap();


/**
 * Geeft de Shopify-route voor cart-wijzigingen terug.
 *
 * We proberen eerst routes uit Deel 1 te gebruiken.
 * Wanneer die niet bestaan, gebruiken we de standaard
 * Shopify-route.
 */
const getCartChangeRoute = () => {
  return (
    state.routes?.cartChange ||
    state.routes?.change ||
    state.routes?.cartChangeJson ||
    `${window.Shopify?.routes?.root || '/'}cart/change.js`
  );
};


/**
 * Zoekt de cart-itemkaart rondom een element.
 */
const getItemCard = (element) => {
  if (!element) return null;

  return element.closest(
    '.tdsc__item, [data-cart-item]'
  );
};


/**
 * Leest de unieke Shopify line-item key.
 *
 * De item key is betrouwbaarder dan alleen het regelnummer,
 * vooral bij producten met properties of personalisaties.
 */
const getItemKey = (element) => {
  const card = getItemCard(element);

  return (
    element?.dataset?.itemKey ||
    card?.dataset?.itemKey ||
    ''
  );
};


/**
 * Leest het Shopify-regelnummer.
 *
 * Shopify gebruikt een 1-based index:
 * het eerste product heeft regelnummer 1.
 */
const getItemIndex = (element) => {
  const card = getItemCard(element);

  const index = Number(
    element?.dataset?.itemIndex ||
    card?.dataset?.itemIndex ||
    0
  );

  return Number.isFinite(index)
    ? index
    : 0;
};


/**
 * Leest het huidige aantal van een cart-item.
 */
const getCurrentItemQuantity = (element) => {
  const card = getItemCard(element);

  const input = card
    ? qs('[data-item-quantity]', card)
    : null;

  const quantity = Number(
    input?.value ||
    card?.dataset?.itemQuantity ||
    0
  );

  if (!Number.isFinite(quantity)) {
    return 0;
  }

  return Math.max(0, Math.floor(quantity));
};


/**
 * Normaliseert een ingevoerd aantal.
 */
const normalizeQuantity = (quantity) => {
  const parsedQuantity = Number(quantity);

  if (!Number.isFinite(parsedQuantity)) {
    return 0;
  }

  return Math.max(
    0,
    Math.floor(parsedQuantity)
  );
};


/**
 * Schakelt alle quantity- en remove-controls tijdelijk uit.
 *
 * Hiermee voorkomen we dubbele Ajax-verzoeken wanneer iemand
 * snel meerdere keren achter elkaar klikt.
 */
const setMutationControlsDisabled = (disabled) => {
  if (!state.root) return;

  qsa(
    [
      '[data-item-decrease]',
      '[data-item-increase]',
      '[data-item-remove]',
      '[data-item-quantity]'
    ].join(','),
    state.root
  ).forEach((control) => {
    control.disabled = disabled;
    control.setAttribute(
      'aria-disabled',
      disabled ? 'true' : 'false'
    );
  });
};


/**
 * Zet één specifieke productkaart in loading-status.
 */
const setItemMutationLoading = (
  card,
  isLoading,
  action = ''
) => {
  if (!card) return;

  card.classList.toggle(
    'is-updating',
    isLoading
  );

  card.classList.toggle(
    'is-removing',
    isLoading && action === 'remove'
  );

  card.setAttribute(
    'aria-busy',
    isLoading ? 'true' : 'false'
  );

  if (isLoading && action) {
    card.dataset.updateAction = action;
  } else {
    delete card.dataset.updateAction;
  }
};


/**
 * Leest een bruikbare foutmelding uit Shopify-responses.
 */
const getCartMutationErrorMessage = (
  error,
  fallbackMessage
) => {
  if (!error) {
    return fallbackMessage;
  }

  if (
    typeof error.description === 'string' &&
    error.description.trim()
  ) {
    return error.description;
  }

  if (
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return error.message;
  }

  if (
    typeof error.errors === 'string' &&
    error.errors.trim()
  ) {
    return error.errors;
  }

  if (
    error.errors &&
    typeof error.errors === 'object'
  ) {
    const errorMessages = Object.values(error.errors)
      .flat()
      .filter(Boolean)
      .map(String);

    if (errorMessages.length) {
      return errorMessages.join(' ');
    }
  }

  return fallbackMessage;
};


/**
 * Verstuur een wijziging naar Shopify.
 *
 * De unieke item key wordt bij voorkeur gebruikt.
 * Wanneer die ontbreekt, gebruiken we het regelnummer.
 */
const requestCartChange = async ({
  itemKey = '',
  itemIndex = 0,
  quantity = 0
} = {}) => {
  const payload = {
    quantity: normalizeQuantity(quantity)
  };

  if (itemKey) {
    payload.id = itemKey;
  } else if (itemIndex > 0) {
    payload.line = itemIndex;
  } else {
    throw new Error(
      'Het winkelwagenproduct kon niet worden herkend.'
    );
  }

  return requestJSON(
    getCartChangeRoute(),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );
};


/**
 * Werkt één cart-item bij.
 *
 * Shopify retourneert na /cart/change.js direct de volledige
 * actuele winkelwagen. Die kunnen we meteen opnieuw renderen.
 */
const changeCartItemQuantity = async ({
  element,
  quantity,
  action = 'quantity'
} = {}) => {
  if (
    cartMutationInProgress ||
    !element
  ) {
    return null;
  }

  const card = getItemCard(element);
  const itemKey = getItemKey(element);
  const itemIndex = getItemIndex(element);
  const nextQuantity = normalizeQuantity(quantity);

  const quantityInput = card
    ? qs('[data-item-quantity]', card)
    : null;

  const previousQuantity = getCurrentItemQuantity(element);
  const previousScrollTop = state.content
    ? state.content.scrollTop
    : 0;

  cartMutationInProgress = true;

  setItemMutationLoading(
    card,
    true,
    action
  );

  setMutationControlsDisabled(true);

  /**
   * Direct de gekozen waarde tonen.
   * Bij een fout wordt de oude waarde teruggezet.
   */
  if (
    quantityInput &&
    action !== 'remove'
  ) {
    quantityInput.value = String(nextQuantity);
  }

  try {
    const cart = await requestCartChange({
      itemKey,
      itemIndex,
      quantity: nextQuantity
    });

    renderCart(cart);

    /**
     * Na het opnieuw renderen wordt de vorige scrollpositie
     * van de drawer zoveel mogelijk hersteld.
     */
    if (state.content) {
      window.requestAnimationFrame(() => {
        state.content.scrollTop =
          previousScrollTop;
      });
    }

    const itemName =
      qs('[data-item-title]', card)?.textContent?.trim() ||
      'Product';

    if (nextQuantity === 0) {
      announce(
        `${itemName} is uit je winkelwagen verwijderd`
      );
    } else {
      announce(
        `Aantal van ${itemName} aangepast naar ${nextQuantity}`
      );
    }

    document.dispatchEvent(
      new CustomEvent('td:cart-changed', {
        detail: {
          cart,
          itemKey,
          itemIndex,
          quantity: nextQuantity,
          action
        }
      })
    );

    return cart;
  } catch (error) {
    console.error(
      '[TD Slide Cart] Product bijwerken mislukt:',
      error
    );

    /**
     * Oude invoerwaarde herstellen wanneer het verzoek mislukt.
     */
    if (quantityInput) {
      quantityInput.value =
        String(previousQuantity);
    }

    setItemMutationLoading(
      card,
      false
    );

    const errorMessage =
      getCartMutationErrorMessage(
        error,
        'Het product kon niet worden bijgewerkt.'
      );

    showNotice(
      errorMessage,
      'error'
    );

    announce(errorMessage);

    /**
     * De actuele Shopify-cart opnieuw ophalen om te voorkomen
     * dat de drawer een verouderde hoeveelheid toont.
     */
    try {
      await refreshCart({
        showLoader: false,
        preserveScroll: true
      });
    } catch (refreshError) {
      console.error(
        '[TD Slide Cart] Cart herstellen mislukt:',
        refreshError
      );
    }

    return null;
  } finally {
    cartMutationInProgress = false;

    setMutationControlsDisabled(false);

    /**
     * Alleen nodig wanneer dezelfde kaart nog in de DOM staat.
     * Bij succesvol renderen is de oude kaart vervangen.
     */
    if (card?.isConnected) {
      setItemMutationLoading(
        card,
        false
      );
    }
  }
};


/**
 * Verlaag de hoeveelheid met één.
 *
 * Wanneer het huidige aantal 1 is, wordt de nieuwe hoeveelheid
 * 0 en verwijdert Shopify het product.
 */
const handleDecreaseItem = (button) => {
  const currentQuantity =
    getCurrentItemQuantity(button);

  const nextQuantity = Math.max(
    0,
    currentQuantity - 1
  );

  return changeCartItemQuantity({
    element: button,
    quantity: nextQuantity,
    action:
      nextQuantity === 0
        ? 'remove'
        : 'decrease'
  });
};


/**
 * Verhoog de hoeveelheid met één.
 */
const handleIncreaseItem = (button) => {
  const currentQuantity =
    getCurrentItemQuantity(button);

  return changeCartItemQuantity({
    element: button,
    quantity: currentQuantity + 1,
    action: 'increase'
  });
};


/**
 * Verwijdert een product volledig.
 */
const handleRemoveItem = (button) => {
  return changeCartItemQuantity({
    element: button,
    quantity: 0,
    action: 'remove'
  });
};


/**
 * Verwerkt een handmatig ingevoerd aantal.
 */
const handleQuantityInputChange = (input) => {
  const quantity =
    normalizeQuantity(input.value);

  input.value = String(quantity);

  return changeCartItemQuantity({
    element: input,
    quantity,
    action:
      quantity === 0
        ? 'remove'
        : 'input'
  });
};


/**
 * Event delegation voor de plus-, min- en verwijderknoppen.
 *
 * De cart-items worden na iedere wijziging opnieuw gerenderd.
 * Door delegation op document te gebruiken hoeven we niet
 * na elke render opnieuw listeners aan knoppen te koppelen.
 */
const handleCartMutationClick = (event) => {
  const decreaseButton =
    event.target.closest('[data-item-decrease]');

  if (decreaseButton) {
    if (
      !state.root?.contains(decreaseButton) ||
      decreaseButton.disabled
    ) {
      return;
    }

    event.preventDefault();

    handleDecreaseItem(decreaseButton)
      .catch(() => {});

    return;
  }

  const increaseButton =
    event.target.closest('[data-item-increase]');

  if (increaseButton) {
    if (
      !state.root?.contains(increaseButton) ||
      increaseButton.disabled
    ) {
      return;
    }

    event.preventDefault();

    handleIncreaseItem(increaseButton)
      .catch(() => {});

    return;
  }

  const removeButton =
    event.target.closest('[data-item-remove]');

  if (removeButton) {
    if (
      !state.root?.contains(removeButton) ||
      removeButton.disabled
    ) {
      return;
    }

    event.preventDefault();

    handleRemoveItem(removeButton)
      .catch(() => {});
  }
};


/**
 * Handmatige quantity-invoer verwerken.
 *
 * De wijziging wordt definitief wanneer:
 * - de gebruiker het invoerveld verlaat;
 * - een change-event wordt uitgevoerd;
 * - Enter wordt ingedrukt.
 */
const handleCartQuantityChange = (event) => {
  const input = event.target.closest(
    '[data-item-quantity]'
  );

  if (
    !input ||
    !state.root?.contains(input) ||
    input.disabled
  ) {
    return;
  }

  const existingTimer =
    quantityInputTimers.get(input);

  if (existingTimer) {
    window.clearTimeout(existingTimer);
    quantityInputTimers.delete(input);
  }

  handleQuantityInputChange(input)
    .catch(() => {});
};


/**
 * Houdt de quantity-input tijdens typen geldig.
 *
 * We wachten kort voordat de Shopify-update wordt uitgevoerd,
 * zodat niet bij iedere toetsaanslag direct een verzoek wordt
 * verzonden.
 */
const handleCartQuantityInput = (event) => {
  const input = event.target.closest(
    '[data-item-quantity]'
  );

  if (
    !input ||
    !state.root?.contains(input) ||
    input.disabled
  ) {
    return;
  }

  const rawValue = String(input.value).trim();

  /**
   * Een tijdelijk leeg veld toestaan tijdens het typen.
   */
  if (rawValue === '') {
    return;
  }

  const quantity =
    normalizeQuantity(rawValue);

  if (String(quantity) !== rawValue) {
    input.value = String(quantity);
  }

  const existingTimer =
    quantityInputTimers.get(input);

  if (existingTimer) {
    window.clearTimeout(existingTimer);
  }

  const timer = window.setTimeout(() => {
    quantityInputTimers.delete(input);

    if (
      input.isConnected &&
      !input.disabled
    ) {
      handleQuantityInputChange(input)
        .catch(() => {});
    }
  }, 700);

  quantityInputTimers.set(
    input,
    timer
  );
};


/**
 * Toetsenbordbediening van quantity-inputs.
 */
const handleCartQuantityKeydown = (event) => {
  const input = event.target.closest(
    '[data-item-quantity]'
  );

  if (
    !input ||
    !state.root?.contains(input)
  ) {
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();

    const existingTimer =
      quantityInputTimers.get(input);

    if (existingTimer) {
      window.clearTimeout(existingTimer);
      quantityInputTimers.delete(input);
    }

    input.blur();
    return;
  }

  /**
   * Voorkomt ongeldige tekens in number-inputs.
   */
  if (
    ['-', '+', 'e', 'E', '.', ','].includes(event.key)
  ) {
    event.preventDefault();
  }
};


/**
 * Wanneer een quantity-input leeg wordt achtergelaten,
 * herstellen we minimaal hoeveelheid 1.
 *
 * Expliciet 0 invoeren blijft wel toegestaan en verwijdert
 * het product.
 */
const handleCartQuantityBlur = (event) => {
  const input = event.target.closest(
    '[data-item-quantity]'
  );

  if (
    !input ||
    !state.root?.contains(input) ||
    input.disabled
  ) {
    return;
  }

  if (String(input.value).trim() === '') {
    input.value = '1';
  }
};


/**
 * Koppel alle events van Deel 3.
 */
document.addEventListener(
  'click',
  handleCartMutationClick
);

document.addEventListener(
  'input',
  handleCartQuantityInput
);

document.addEventListener(
  'change',
  handleCartQuantityChange
);

document.addEventListener(
  'keydown',
  handleCartQuantityKeydown
);

document.addEventListener(
  'blur',
  handleCartQuantityBlur,
  true
);


/**
 * Publieke functies beschikbaar maken voor latere delen
 * en eventuele externe Shopify-integraties.
 */
api.changeItem = changeCartItemQuantity;

api.removeItem = ({
  itemKey = '',
  itemIndex = 0
} = {}) => {
  const card =
    qsa('.tdsc__item', state.root).find((itemCard) => {
      if (
        itemKey &&
        itemCard.dataset.itemKey === itemKey
      ) {
        return true;
      }

      return (
        itemIndex > 0 &&
        Number(itemCard.dataset.itemIndex) ===
          Number(itemIndex)
      );
    }) || null;

  if (!card) {
    return Promise.reject(
      new Error(
        'Het winkelwagenproduct kon niet worden gevonden.'
      )
    );
  }

  return changeCartItemQuantity({
    element: card,
    quantity: 0,
    action: 'remove'
  });
};


/* =========================================================
   EINDE DEEL 3 VAN 5

   DEEL 4 HIER DIRECT ONDER PLAKKEN.
   ========================================================= */

/* =========================================================
   TD PREMIUM SLIDE CART — V1
   DEEL 4 VAN 5

   Ajax add-to-cart, productformulieren en automatisch openen.

   PLAATSING:
   Plak dit direct onder DEEL 3 in assets/td-slide-cart.js.
   ========================================================= */


/**
 * Houdt bij of er momenteel een product wordt toegevoegd.
 *
 * Hiermee voorkomen we dubbele bestellingen wanneer iemand
 * snel meerdere keren op de koopknop klikt.
 */
let addToCartInProgress = false;


/**
 * Selectors van Shopify-productformulieren.
 *
 * We gebruiken meerdere selectors zodat dit ook werkt met:
 * - standaard Shopify product forms;
 * - quick-add formulieren;
 * - product cards;
 * - aangepaste TegeltjeDruk-formulieren.
 */
const productFormSelector = [
  'form[action*="/cart/add"]',
  'form[data-type="add-to-cart-form"]',
  'product-form form',
  '[data-product-form]'
].join(',');


/**
 * Selectors van bekende add-to-cart knoppen.
 */
const addToCartButtonSelector = [
  '[name="add"]',
  '[type="submit"][data-add-to-cart]',
  '.product-form__submit',
  '.quick-add__submit',
  '[data-td-add-to-cart]'
].join(',');


/**
 * Geeft de Shopify add-to-cart route terug.
 */
const getCartAddRoute = () => {
  const shopifyRoot =
    window.Shopify?.routes?.root ||
    '/';

  return (
    state.routes?.cartAdd ||
    state.routes?.add ||
    state.routes?.cartAddJson ||
    `${shopifyRoot}cart/add.js`
  );
};


/**
 * Controleert of een formulier echt een cart-add formulier is.
 */
const isProductForm = (form) => {
  if (
    !form ||
    !(form instanceof HTMLFormElement)
  ) {
    return false;
  }

  const action =
    form.getAttribute('action') ||
    '';

  return (
    action.includes('/cart/add') ||
    form.matches('[data-product-form]') ||
    Boolean(form.closest('product-form')) ||
    form.dataset.type === 'add-to-cart-form'
  );
};


/**
 * Zoekt de knop waarmee het formulier werd verzonden.
 */
const getFormSubmitButton = (
  form,
  submitter = null
) => {
  if (
    submitter &&
    submitter.matches?.(addToCartButtonSelector)
  ) {
    return submitter;
  }

  return qs(
    addToCartButtonSelector,
    form
  );
};


/**
 * Leest de oorspronkelijke knoptekst.
 */
const getButtonTextElement = (button) => {
  if (!button) return null;

  return (
    qs('[data-add-to-cart-text]', button) ||
    qs('.product-form__submit-label', button) ||
    qs('span', button) ||
    button
  );
};


/**
 * Zet een add-to-cart knop in loading-status.
 */
const setAddToCartButtonLoading = (
  button,
  isLoading
) => {
  if (!button) return;

  const textElement =
    getButtonTextElement(button);

  if (isLoading) {
    if (!button.dataset.originalText) {
      button.dataset.originalText =
        textElement?.textContent?.trim() ||
        button.textContent?.trim() ||
        'Toevoegen';
    }

    button.disabled = true;
    button.classList.add('is-loading');

    button.setAttribute(
      'aria-disabled',
      'true'
    );

    button.setAttribute(
      'aria-busy',
      'true'
    );

    if (textElement) {
      textElement.textContent =
        'Toevoegen...';
    }

    return;
  }

  button.disabled = false;
  button.classList.remove('is-loading');

  button.setAttribute(
    'aria-disabled',
    'false'
  );

  button.setAttribute(
    'aria-busy',
    'false'
  );

  if (
    textElement &&
    button.dataset.originalText
  ) {
    textElement.textContent =
      button.dataset.originalText;
  }

  delete button.dataset.originalText;
};


/**
 * Verwijdert eventuele oude foutmeldingen uit een productform.
 */
const clearProductFormError = (form) => {
  if (!form) return;

  const errorElements = qsa(
    [
      '[data-product-form-error]',
      '.product-form__error-message-wrapper',
      '.product-form__error-message',
      '.tdsc-product-error'
    ].join(','),
    form
  );

  errorElements.forEach((element) => {
    element.hidden = true;
    element.classList.remove('is-visible');

    if (
      element.matches(
        '[data-product-form-error], .tdsc-product-error'
      )
    ) {
      element.textContent = '';
    }
  });
};


/**
 * Zoekt of maakt een foutmelding onder het productformulier.
 */
const getProductFormErrorElement = (form) => {
  if (!form) return null;

  const existingElement =
    qs('[data-product-form-error]', form) ||
    qs('.product-form__error-message', form) ||
    qs('.tdsc-product-error', form);

  if (existingElement) {
    return existingElement;
  }

  const errorElement =
    document.createElement('div');

  errorElement.className =
    'tdsc-product-error';

  errorElement.dataset.productFormError = '';

  errorElement.setAttribute(
    'role',
    'alert'
  );

  errorElement.setAttribute(
    'aria-live',
    'polite'
  );

  errorElement.hidden = true;

  form.appendChild(errorElement);

  return errorElement;
};


/**
 * Toont een foutmelding bij het productformulier.
 */
const showProductFormError = (
  form,
  message
) => {
  const errorElement =
    getProductFormErrorElement(form);

  if (errorElement) {
    errorElement.textContent = message;
    errorElement.hidden = false;
    errorElement.classList.add('is-visible');
  }

  showNotice(
    message,
    'error'
  );

  announce(message);
};


/**
 * Leest een foutmelding uit een Shopify response.
 */
const getAddToCartErrorMessage = (
  error,
  fallbackMessage =
    'Het product kon niet worden toegevoegd.'
) => {
  if (!error) {
    return fallbackMessage;
  }

  if (
    typeof error.description === 'string' &&
    error.description.trim()
  ) {
    return error.description;
  }

  if (
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return error.message;
  }

  if (
    typeof error.errors === 'string' &&
    error.errors.trim()
  ) {
    return error.errors;
  }

  if (
    error.errors &&
    typeof error.errors === 'object'
  ) {
    const messages = Object.values(error.errors)
      .flat()
      .filter(Boolean)
      .map(String);

    if (messages.length) {
      return messages.join(' ');
    }
  }

  return fallbackMessage;
};


/**
 * Controleert of een FormData-object een variant-ID bevat.
 */
const hasVariantId = (formData) => {
  const variantId =
    formData.get('id');

  return Boolean(
    variantId &&
    String(variantId).trim()
  );
};


/**
 * Maakt FormData geschikt voor Shopify.
 *
 * Alle invoervelden, line-item properties, uploadvelden,
 * quantity en variantinformatie blijven behouden.
 */
const createCartAddFormData = (
  form,
  submitter = null
) => {
  let formData;

  try {
    formData = submitter
      ? new FormData(form, submitter)
      : new FormData(form);
  } catch (error) {
    formData = new FormData(form);
  }

  /**
   * Sommige browsers voegen de geklikte submitknop niet
   * automatisch toe aan FormData.
   */
  if (
    submitter?.name &&
    !formData.has(submitter.name)
  ) {
    formData.append(
      submitter.name,
      submitter.value || ''
    );
  }

  /**
   * Quantity standaard op 1 zetten wanneer het veld ontbreekt.
   */
  if (!formData.has('quantity')) {
    formData.append('quantity', '1');
  }

  return formData;
};


/**
 * Opent de slide cart via de publieke API uit Deel 1.
 *
 * We ondersteunen meerdere functienamen, zodat het deel ook
 * blijft werken wanneer Deel 1 een iets andere naam gebruikt.
 */
const openSlideCartAfterAdd = () => {
  if (typeof api.open === 'function') {
    api.open();
    return;
  }

  if (typeof api.openCart === 'function') {
    api.openCart();
    return;
  }

  if (typeof api.show === 'function') {
    api.show();
    return;
  }

  /**
   * Externe fallback voor aangepaste implementaties.
   */
  document.dispatchEvent(
    new CustomEvent('td:open-cart')
  );

  document.dispatchEvent(
    new CustomEvent('cart:open')
  );
};


/**
 * Voegt FormData toe via Shopify Ajax.
 */
const requestAddToCart = async (
  formData
) => {
  return requestJSON(
    getCartAddRoute(),
    {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      },
      body: formData
    }
  );
};


/**
 * Centrale add-to-cart functie.
 */
const addProductToCart = async ({
  form = null,
  formData = null,
  submitter = null,
  openCart = true
} = {}) => {
  if (addToCartInProgress) {
    return null;
  }

  if (
    !formData &&
    !form
  ) {
    throw new Error(
      'Er zijn geen productgegevens ontvangen.'
    );
  }

  const activeFormData =
    formData ||
    createCartAddFormData(
      form,
      submitter
    );

  if (!hasVariantId(activeFormData)) {
    const errorMessage =
      'Selecteer eerst een productoptie.';

    if (form) {
      showProductFormError(
        form,
        errorMessage
      );
    }

    throw new Error(errorMessage);
  }

  const button =
    form
      ? getFormSubmitButton(
          form,
          submitter
        )
      : null;

  addToCartInProgress = true;

  if (form) {
    clearProductFormError(form);
  }

  setAddToCartButtonLoading(
    button,
    true
  );

  try {
    const addedItem =
      await requestAddToCart(
        activeFormData
      );

    /**
     * Eerst de actuele winkelwagen ophalen en renderen.
     */
    const cart = await refreshCart({
      showLoader: false,
      preserveScroll: false
    });

    if (openCart) {
      openSlideCartAfterAdd();
    }

    const productName =
      addedItem?.product_title ||
      addedItem?.title ||
      'Product';

    announce(
      `${productName} is toegevoegd aan je winkelwagen`
    );

    document.dispatchEvent(
      new CustomEvent('td:product-added', {
        detail: {
          item: addedItem,
          cart,
          form
        }
      })
    );

    return {
      item: addedItem,
      cart
    };
  } catch (error) {
    console.error(
      '[TD Slide Cart] Toevoegen mislukt:',
      error
    );

    const errorMessage =
      getAddToCartErrorMessage(error);

    if (form) {
      showProductFormError(
        form,
        errorMessage
      );
    } else {
      showNotice(
        errorMessage,
        'error'
      );

      announce(errorMessage);
    }

    throw error;
  } finally {
    addToCartInProgress = false;

    setAddToCartButtonLoading(
      button,
      false
    );
  }
};


/**
 * Onderschept Shopify productformulieren.
 */
const handleProductFormSubmit = (event) => {
  const form =
    event.target.closest?.(
      productFormSelector
    );

  if (
    !form ||
    !isProductForm(form)
  ) {
    return;
  }

  /**
   * Formulieren kunnen zichzelf uitsluiten wanneer een
   * externe app de volledige afhandeling moet verzorgen.
   */
  if (
    form.hasAttribute(
      'data-td-native-cart'
    ) ||
    form.dataset.tdAjaxCart === 'false'
  ) {
    return;
  }

  /**
   * Voorkom dat onze eigen verzoeken opnieuw worden verwerkt.
   */
  if (
    form.dataset.tdSubmitting === 'true'
  ) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const submitter =
    event.submitter ||
    document.activeElement?.closest?.(
      addToCartButtonSelector
    ) ||
    null;

  form.dataset.tdSubmitting = 'true';

  addProductToCart({
    form,
    submitter,
    openCart: true
  })
    .catch(() => {})
    .finally(() => {
      delete form.dataset.tdSubmitting;
    });
};


/**
 * Ondersteuning voor knoppen die geen echt formulier gebruiken.
 *
 * Vereiste data:
 *
 * data-td-add-to-cart
 * data-variant-id="123456789"
 *
 * Optioneel:
 *
 * data-quantity="2"
 */
const handleStandaloneAddButton = (event) => {
  const button = event.target.closest(
    '[data-td-add-to-cart][data-variant-id]'
  );

  if (
    !button ||
    button.closest(productFormSelector)
  ) {
    return;
  }

  event.preventDefault();

  if (
    button.disabled ||
    addToCartInProgress
  ) {
    return;
  }

  const variantId =
    button.dataset.variantId;

  const quantity = Math.max(
    1,
    Number(button.dataset.quantity) || 1
  );

  const formData =
    new FormData();

  formData.append(
    'id',
    variantId
  );

  formData.append(
    'quantity',
    String(quantity)
  );

  addProductToCart({
    formData,
    submitter: button,
    openCart: true
  }).catch(() => {});
};


/**
 * Externe apps kunnen aangeven dat een product succesvol
 * is toegevoegd.
 *
 * We verversen dan de cart en openen de drawer.
 */
const handleExternalProductAdded = () => {
  refreshCart({
    showLoader: false,
    preserveScroll: false
  })
    .then(() => {
      openSlideCartAfterAdd();
    })
    .catch(() => {});
};


/**
 * Voorkomt dat ons eigen td:product-added event opnieuw
 * een tweede refresh veroorzaakt.
 */
const handleGenericProductAdded = (event) => {
  if (
    event.type === 'td:product-added'
  ) {
    return;
  }

  handleExternalProductAdded();
};


/**
 * Koppel Deel 4.
 *
 * Capture=true zorgt ervoor dat we het productformulier
 * onderscheppen voordat het Shopify-thema de pagina herlaadt.
 */
document.addEventListener(
  'submit',
  handleProductFormSubmit,
  true
);

document.addEventListener(
  'click',
  handleStandaloneAddButton
);


/**
 * Veelgebruikte events van Shopify-thema’s en productapps.
 */
document.addEventListener(
  'product:added',
  handleGenericProductAdded
);

document.addEventListener(
  'ajaxProduct:added',
  handleGenericProductAdded
);

document.addEventListener(
  'cart:item-added',
  handleGenericProductAdded
);


/**
 * Publieke Deel 4 API.
 *
 * Voorbeeld:
 *
 * window.TDSlideCart.addItem({
 *   id: 123456789,
 *   quantity: 1
 * });
 */
api.addItem = async (
  itemData,
  {
    openCart = true
  } = {}
) => {
  const formData =
    new FormData();

  if (
    itemData instanceof FormData
  ) {
    return addProductToCart({
      formData: itemData,
      openCart
    });
  }

  if (
    !itemData ||
    typeof itemData !== 'object'
  ) {
    throw new Error(
      'Ongeldige productgegevens.'
    );
  }

  Object.entries(itemData).forEach(
    ([key, value]) => {
      if (
        value === undefined ||
        value === null
      ) {
        return;
      }

      /**
       * Properties object automatisch omzetten naar
       * Shopify properties[naam]-velden.
       */
      if (
        key === 'properties' &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        Object.entries(value).forEach(
          ([propertyName, propertyValue]) => {
            formData.append(
              `properties[${propertyName}]`,
              String(propertyValue)
            );
          }
        );

        return;
      }

      formData.append(
        key,
        String(value)
      );
    }
  );

  if (!formData.has('quantity')) {
    formData.append(
      'quantity',
      '1'
    );
  }

  return addProductToCart({
    formData,
    openCart
  });
};


/**
 * Cart handmatig verversen en openen.
 */
api.refreshAndOpen = async () => {
  const cart = await refreshCart({
    showLoader: false,
    preserveScroll: false
  });

  openSlideCartAfterAdd();

  return cart;
};


/* =========================================================
   EINDE DEEL 4 VAN 5

   DEEL 5 HIER DIRECT ONDER PLAKKEN.
   ========================================================= */

/* =========================================================
   TD PREMIUM SLIDE CART — V1
   DEEL 5 VAN 5

   Premium afwerking, verzending, checkout en accessibility.

   PLAATSING:
   Vervang het bestaande DEEL 5 volledig door deze code.
   ========================================================= */


/* =========================================================
   INSTELLINGEN
   ========================================================= */

const FREE_SHIPPING_THRESHOLD =
  Number(
    window.TDSlideCartConfig?.freeShippingThreshold
  ) || 6000;


const shippingProgressSelectors = {
  root: [
    '[data-shipping-progress]',
    '[data-td-shipping-progress]',
    '.tdsc__shipping-progress'
  ].join(','),

  bar: [
    '[data-shipping-progress-bar]',
    '[data-td-shipping-progress-bar]',
    '.tdsc__shipping-progress-bar'
  ].join(','),

  text: [
    '[data-shipping-progress-text]',
    '[data-td-shipping-progress-text]',
    '.tdsc__shipping-progress-text'
  ].join(',')
};


const checkoutButtonSelector = [
  '[data-cart-checkout]',
  '[data-td-checkout]',
  '.tdsc__checkout',
  'button[name="checkout"]',
  'a[href*="/checkout"]'
].join(',');


const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');


/* =========================================================
   GRATIS VERZENDING
   ========================================================= */

const updateShippingProgress = (cart) => {
  if (!state.root) return;

  const progressRoot = qs(
    '[data-shipping-progress]',
    state.root
  );

  if (!progressRoot) return;

  const progressTrack = qs(
    '[data-shipping-progress-track]',
    progressRoot
  );

  const progressMask = qs(
    '[data-shipping-progress-mask]',
    progressRoot
  );

  const progressMarker = qs(
    '[data-shipping-progress-marker]',
    progressRoot
  );

  const progressText = qs(
    '[data-shipping-progress-text]',
    progressRoot
  );

  const progressMessage = qs(
    '[data-shipping-progress-message]',
    progressRoot
  );

  const progressPercentage = qs(
    '[data-shipping-progress-percentage]',
    progressRoot
  );

  const items = Array.isArray(cart?.items)
    ? cart.items
    : [];

  const cartTotal = Math.max(
    0,
    Number(cart?.total_price) || 0
  );

  const threshold = Math.max(
    1,
    Number(
      progressRoot.dataset.shippingThreshold ||
      state.root.dataset.shippingThreshold ||
      window.TDSlideCartConfig?.freeShippingThreshold ||
      6000
    ) || 6000
  );

  const remaining = Math.max(
    0,
    threshold - cartTotal
  );

  const percentage = Math.min(
    100,
    Math.max(0, (cartTotal / threshold) * 100)
  );

  const roundedPercentage =
    Math.round(percentage);

  const isComplete =
    cartTotal >= threshold;

  progressRoot.hidden =
    items.length === 0;

  progressRoot.classList.remove(
    'is-low',
    'is-half',
    'is-high',
    'is-complete'
  );

  if (isComplete) {
    progressRoot.classList.add('is-complete');
  } else if (percentage < 33) {
    progressRoot.classList.add('is-low');
  } else if (percentage < 66) {
    progressRoot.classList.add('is-half');
  } else {
    progressRoot.classList.add('is-high');
  }

  progressTrack?.setAttribute(
    'aria-valuenow',
    String(roundedPercentage)
  );

  if (progressMask) {
    progressMask.style.left = `${percentage}%`;
  }

  if (progressMarker) {
    progressMarker.style.left = `${percentage}%`;
  }

  if (progressPercentage) {
    progressPercentage.textContent =
      `${roundedPercentage}%`;
  }

  if (isComplete) {
    if (progressText) {
      progressText.textContent =
        'Gratis verzending is bereikt!';
    }

    if (progressMessage) {
      progressMessage.textContent =
        'Je bestelling wordt gratis verzonden.';
    }
  } else {
    if (progressText) {
      progressText.textContent =
        `Nog ${formatMoney(remaining)} tot gratis verzending.`;
    }

    if (progressMessage) {
      progressMessage.textContent =
        `Voeg nog ${formatMoney(remaining)} toe aan je bestelling.`;
    }
  }
};


/* =========================================================
   CHECKOUT
   ========================================================= */

const cartHasItems = () => {
  return Boolean(
    state.cart &&
    Array.isArray(state.cart.items) &&
    state.cart.items.length
  );
};


const setCheckoutLoading = (
  button,
  isLoading
) => {
  if (!button) return;

  const textElement =
    qs('[data-checkout-text]', button) ||
    qs('span', button) ||
    button;

  if (isLoading) {
    if (!button.dataset.originalText) {
      button.dataset.originalText =
        textElement?.textContent?.trim() ||
        'Afrekenen';
    }

    button.classList.add('is-loading');

    button.setAttribute(
      'aria-busy',
      'true'
    );

    button.setAttribute(
      'aria-disabled',
      'true'
    );

    if ('disabled' in button) {
      button.disabled = true;
    }

    if (textElement) {
      textElement.textContent =
        'Doorsturen...';
    }

    return;
  }

  button.classList.remove('is-loading');
  button.removeAttribute('aria-busy');

  button.setAttribute(
    'aria-disabled',
    'false'
  );

  if ('disabled' in button) {
    button.disabled = false;
  }

  if (
    textElement &&
    button.dataset.originalText
  ) {
    textElement.textContent =
      button.dataset.originalText;
  }

  delete button.dataset.originalText;
};


const handleCheckoutClick = (event) => {
  const checkoutButton =
    event.target.closest(
      checkoutButtonSelector
    );

  if (
    !checkoutButton ||
    !state.root?.contains(checkoutButton)
  ) {
    return;
  }

  if (!cartHasItems()) {
    event.preventDefault();

    showNotice(
      'Je winkelwagen is nog leeg.',
      'error'
    );

    announce(
      'Je winkelwagen is nog leeg'
    );

    return;
  }

  if (
    checkoutButton.classList.contains(
      'is-loading'
    )
  ) {
    event.preventDefault();
    return;
  }

  setCheckoutLoading(
    checkoutButton,
    true
  );

  if (
    checkoutButton instanceof
      HTMLAnchorElement
  ) {
    return;
  }

  const checkoutForm =
    checkoutButton.closest('form');

  if (
    !checkoutForm ||
    !checkoutButton.name
  ) {
    event.preventDefault();

    window.location.href =
      window.TDSlideCartConfig?.routes?.checkout ||
      `${window.Shopify?.routes?.root || '/'}checkout`;
  }
};


/* =========================================================
   FOCUSBEHEER
   ========================================================= */

let previouslyFocusedElement = null;


const getPart5FocusableElements = () => {
  if (!state.root) return [];

  return qsa(
    focusableSelector,
    state.root
  ).filter((element) => {
    if (
      element.hidden ||
      element.getAttribute('aria-hidden') === 'true'
    ) {
      return false;
    }

    return Boolean(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  });
};


const focusSlideCart = () => {
  if (!state.root) return;

  const preferredFocus =
    qs('[data-cart-close]', state.root) ||
    qs('[data-td-cart-close]', state.root) ||
    qs('.tdsc__close', state.root);

  const firstFocusable =
    getPart5FocusableElements()[0];

  const focusTarget =
    preferredFocus ||
    firstFocusable ||
    state.root;

  if (
    focusTarget === state.root &&
    !focusTarget.hasAttribute('tabindex')
  ) {
    focusTarget.setAttribute(
      'tabindex',
      '-1'
    );
  }

  window.requestAnimationFrame(() => {
    focusTarget.focus({
      preventScroll: true
    });
  });
};


const restorePreviousFocus = () => {
  if (
    previouslyFocusedElement &&
    document.contains(
      previouslyFocusedElement
    ) &&
    typeof previouslyFocusedElement.focus ===
      'function'
  ) {
    previouslyFocusedElement.focus({
      preventScroll: true
    });
  }

  previouslyFocusedElement = null;
};


const trapSlideCartFocus = (event) => {
  if (
    event.key !== 'Tab' ||
    !state.isOpen ||
    !state.root
  ) {
    return;
  }

  const focusableElements =
    getPart5FocusableElements();

  if (!focusableElements.length) {
    event.preventDefault();
    state.root.focus();
    return;
  }

  const firstElement =
    focusableElements[0];

  const lastElement =
    focusableElements[
      focusableElements.length - 1
    ];

  const activeElement =
    document.activeElement;

  if (
    event.shiftKey &&
    (
      activeElement === firstElement ||
      !state.root.contains(activeElement)
    )
  ) {
    event.preventDefault();
    lastElement.focus();
    return;
  }

  if (
    !event.shiftKey &&
    activeElement === lastElement
  ) {
    event.preventDefault();
    firstElement.focus();
  }
};


const handleSlideCartEscape = (event) => {
  if (
    event.key !== 'Escape' ||
    !state.isOpen
  ) {
    return;
  }

  event.preventDefault();

  if (typeof api.close === 'function') {
    api.close();
    return;
  }

  if (
    typeof api.closeCart === 'function'
  ) {
    api.closeCart();
    return;
  }

  document.dispatchEvent(
    new CustomEvent('td:close-cart')
  );
};


/* =========================================================
   OPEN- EN SLUITFUNCTIES UITBREIDEN
   ========================================================= */

if (typeof api.open === 'function') {
  const originalOpenPart5 =
    api.open.bind(api);

  api.open = (...args) => {
    previouslyFocusedElement =
      document.activeElement;

    const result =
      originalOpenPart5(...args);

    window.requestAnimationFrame(
      focusSlideCart
    );

    return result;
  };
}


if (typeof api.close === 'function') {
  const originalClosePart5 =
    api.close.bind(api);

  api.close = (...args) => {
    const result =
      originalClosePart5(...args);

    window.requestAnimationFrame(
      restorePreviousFocus
    );

    return result;
  };
}


if (
  typeof api.open !== 'function' &&
  typeof api.openCart === 'function'
) {
  const originalOpenCartPart5 =
    api.openCart.bind(api);

  api.openCart = (...args) => {
    previouslyFocusedElement =
      document.activeElement;

    const result =
      originalOpenCartPart5(...args);

    window.requestAnimationFrame(
      focusSlideCart
    );

    return result;
  };
}


if (
  typeof api.close !== 'function' &&
  typeof api.closeCart === 'function'
) {
  const originalCloseCartPart5 =
    api.closeCart.bind(api);

  api.closeCart = (...args) => {
    const result =
      originalCloseCartPart5(...args);

    window.requestAnimationFrame(
      restorePreviousFocus
    );

    return result;
  };
}


/* =========================================================
   CART EVENTS
   ========================================================= */

document.addEventListener(
  'td:cart-rendered',
  (event) => {
    updateShippingProgress(
      event.detail?.cart ||
      state.cart
    );
  }
);


document.addEventListener(
  'td:cart-changed',
  (event) => {
    updateShippingProgress(
      event.detail?.cart ||
      state.cart
    );
  }
);


document.addEventListener(
  'td:product-added',
  (event) => {
    updateShippingProgress(
      event.detail?.cart ||
      state.cart
    );
  }
);


const handleExternalCartUpdatePart5 = () => {
  refreshCart({
    showLoader: false,
    preserveScroll: true
  }).catch(() => {});
};


document.addEventListener(
  'cart:updated',
  handleExternalCartUpdatePart5
);

document.addEventListener(
  'cart:update',
  handleExternalCartUpdatePart5
);

document.addEventListener(
  'shopify:cart:updated',
  handleExternalCartUpdatePart5
);


/* =========================================================
   PAGINASTATUS
   ========================================================= */

const handleDocumentVisibilityPart5 = () => {
  if (
    document.visibilityState !==
      'visible' ||
    !state.isOpen
  ) {
    return;
  }

  refreshCart({
    showLoader: false,
    preserveScroll: true
  }).catch(() => {});
};


document.addEventListener(
  'visibilitychange',
  handleDocumentVisibilityPart5
);


/* =========================================================
   SHOPIFY THEME EDITOR
   ========================================================= */

const handleShopifySectionLoad = () => {
  if (typeof api.init === 'function') {
    api.init();
    return;
  }

  if (
    typeof api.refreshElements ===
      'function'
  ) {
    api.refreshElements();
  }
};


document.addEventListener(
  'shopify:section:load',
  handleShopifySectionLoad
);


/* =========================================================
   EVENT LISTENERS
   ========================================================= */

document.addEventListener(
  'click',
  handleCheckoutClick
);

document.addEventListener(
  'keydown',
  trapSlideCartFocus
);

document.addEventListener(
  'keydown',
  handleSlideCartEscape
);


/* =========================================================
   PUBLIEKE API
   ========================================================= */

api.getCart = () => {
  return state.cart;
};


api.getItemCount = () => {
  return Math.max(
    0,
    Number(state.cart?.item_count) || 0
  );
};


api.isOpen = () => {
  return Boolean(state.isOpen);
};


api.getShippingProgress = () => {
  const total = Math.max(
    0,
    Number(state.cart?.total_price) || 0
  );

  const remaining = Math.max(
    0,
    FREE_SHIPPING_THRESHOLD - total
  );

  const percentage = Math.min(
    100,
    Math.max(
      0,
      (
        total /
        FREE_SHIPPING_THRESHOLD
      ) * 100
    )
  );

  return {
    threshold:
      FREE_SHIPPING_THRESHOLD,
    total,
    remaining,
    qualified:
      total >=
      FREE_SHIPPING_THRESHOLD,
    percentage
  };
};


api.clear = async () => {
  const clearRoute =
    state.routes?.cartClear ||
    state.routes?.clear ||
    window.TDSlideCartConfig?.routes
      ?.cartClear ||
    `${window.Shopify?.routes?.root || '/'}cart/clear.js`;

  try {
    setLoading(true);

    const cart = await requestJSON(
      clearRoute,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type':
            'application/json'
        }
      }
    );

    renderCart(cart);

    announce(
      'Je winkelwagen is leeggemaakt'
    );

    document.dispatchEvent(
      new CustomEvent(
        'td:cart-cleared',
        {
          detail: {
            cart
          }
        }
      )
    );

    return cart;
  } catch (error) {
    console.error(
      '[TD Slide Cart] Cart leegmaken mislukt:',
      error
    );

    showNotice(
      error.message ||
      'De winkelwagen kon niet worden leeggemaakt.',
      'error'
    );

    throw error;
  } finally {
    setLoading(false);
  }
};


api.checkout = () => {
  if (!cartHasItems()) {
    showNotice(
      'Je winkelwagen is nog leeg.',
      'error'
    );

    return false;
  }

  window.location.href =
    window.TDSlideCartConfig?.routes
      ?.checkout ||
    `${window.Shopify?.routes?.root || '/'}checkout`;

  return true;
};


/* =========================================================
   DEFINITIEVE INITIALISATIE
   ========================================================= */

const initializeSlideCartPart5 = () => {
  if (!state.root) {
    console.warn(
      '[TD Slide Cart] De slide-cart root is niet gevonden.'
    );

    return;
  }

  if (!state.root.hasAttribute('role')) {
    state.root.setAttribute(
      'role',
      'dialog'
    );
  }

  if (
    !state.root.hasAttribute(
      'aria-label'
    )
  ) {
    state.root.setAttribute(
      'aria-label',
      'Winkelwagen'
    );
  }

  updateShippingProgress(
    state.cart || {
      items: [],
      total_price: 0
    }
  );

  qsa(
    checkoutButtonSelector,
    state.root
  ).forEach((button) => {
    setCheckoutLoading(
      button,
      false
    );
  });

  document.dispatchEvent(
    new CustomEvent(
      'td:slide-cart-ready',
      {
        detail: {
          api,
          state
        }
      }
    )
  );

  console.info(
    '[TD Slide Cart] Premium Slide Cart V1 is geladen.'
  );
};


if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeSlideCartPart5,
    {
      once: true
    }
  );
} else {
  initializeSlideCartPart5();
}

/* =========================================================
   HEADER WINKELWAGENKNOP KOPPELEN
   ========================================================= */

const findTDHeaderCartTrigger = (target) => {
  if (!(target instanceof Element)) return null;

  const clickable = target.closest(
    'a, button, [role="button"], cart-drawer-opener, cart-icon'
  );

  if (!clickable || state.root?.contains(clickable)) return null;

  const inHeader = clickable.closest(
    'header, .header, [data-header], #shopify-section-header, .shopify-section-group-header-group'
  );

  if (!inHeader) return null;

  const signature = [
    clickable.id,
    typeof clickable.className === 'string' ? clickable.className : '',
    clickable.getAttribute('href'),
    clickable.getAttribute('aria-label'),
    clickable.getAttribute('title'),
    clickable.getAttribute('data-action'),
    clickable.getAttribute('data-cart-trigger')
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const hasCartMeaning =
    signature.includes('cart') ||
    signature.includes('winkelwagen') ||
    signature.includes('/cart');

  if (hasCartMeaning) return clickable;

  /* Tinker fallback: cart badge/icon inside the clickable header control. */
  if (clickable.querySelector(
    '.cart-count-bubble, [data-cart-count-bubble], [data-cart-count], svg[class*="cart"], [class*="cart-icon"]'
  )) {
    return clickable;
  }

  return null;
};

const handleTDHeaderCartClick = (event) => {
  const trigger = findTDHeaderCartTrigger(event.target);
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  api.open({ refresh: false });

  api.refresh?.({
    showLoader: true,
    preserveScroll: false
  }).catch((error) => {
    console.error('[TD Slide Cart] Header cart openen mislukt:', error);
  });
};

document.addEventListener('click', handleTDHeaderCartClick, true);


/* =========================================================
   TD SLIDE CART — VOORKOM DOORSTUREN NAAR /CART
   ========================================================= */

/**
 * Sommige Shopify-thema's koppelen zelf een click-handler aan
 * de koopknop en sturen daarna alsnog door naar /cart.
 *
 * Deze capture-listener onderschept de koopknop vóór de meeste
 * themahandlers en handelt het toevoegen volledig via onze
 * bestaande Ajax-functie af.
 */
const handleAjaxAddButtonClick = (event) => {
  const button = event.target.closest(
    addToCartButtonSelector
  );

  if (!button) return;

  const form = button.closest(
    productFormSelector
  );

  if (
    !form ||
    !isProductForm(form) ||
    form.hasAttribute('data-td-native-cart') ||
    form.dataset.tdAjaxCart === 'false'
  ) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  if (
    button.disabled ||
    addToCartInProgress ||
    form.dataset.tdSubmitting === 'true'
  ) {
    return;
  }

  form.dataset.tdSubmitting = 'true';

  addProductToCart({
    form,
    submitter: button,
    openCart: true
  })
    .catch(() => {})
    .finally(() => {
      delete form.dataset.tdSubmitting;
    });
};


/**
 * Capture=true zorgt ervoor dat de koopknop wordt onderschept
 * voordat handlers op product-form, button of andere wrappers
 * een gewone cart-navigatie kunnen starten.
 */
document.addEventListener(
  'click',
  handleAjaxAddButtonClick,
  true
);


/* =========================================================
   EINDE DEEL 5 VAN 5

   TD PREMIUM SLIDE CART — V1 COMPLEET
   ========================================================= */

})();
